/**
 * Backend permission names, spelled exactly as the backend defines them —
 * including the `aggreement` typo and the capitalised `Portfolio`. The same
 * spelling is already used by `resend_new_Portfolio_aggreement` in
 * portfolio-agreement.component.tsx, so this is the module's convention, not
 * a mistake to correct here: "fixing" it would silently break the gate.
 */
export const INITIATE_PERMISSION = "initiate_new_Portfolio_aggreement";
export const REVIEW_PERMISSION = "review_new_Portfolio_aggreement";

/** `mode` query parameter on the upload endpoint. */
export type BulkMode = "validate" | "execute";

/**
 * A row the backend accepted or rejected. The field names are the backend's,
 * so nothing here pins them down — the tables render whatever comes back.
 */
export type BulkRecord = Record<string, unknown>;

export interface BulkCounts {
  total?: number;
  valid?: number;
  excluded?: number;
  inserted?: number;
}

/**
 * `POST /portfolio/new-portfolio-agreements/upload?mode=…`
 *
 * `inserted` is only meaningful for execute mode. Counts are read defensively
 * because they may arrive at the top level or nested under a summary object.
 */
export interface BulkUploadResponse extends BulkCounts {
  validRecords?: BulkRecord[];
  excludedRecords?: BulkRecord[];
  counts?: BulkCounts;
  summary?: BulkCounts;
  message?: string;
}

export interface BulkResult {
  mode: BulkMode;
  counts: BulkCounts;
  validRecords: BulkRecord[];
  excludedRecords: BulkRecord[];
}

/** Agreement types the template accepts. `BOTH` expands into two records. */
export const AGREEMENT_TYPES = ["AMENDMENT", "APPENDIX", "BOTH"] as const;

/**
 * Field names the backend might use for a row's exclusion reasons. The first
 * one present wins; anything else still shows as an ordinary column.
 */
export const REASON_KEYS = [
  "exclusionReasons",
  "reasons",
  "exclusionReason",
  "reason",
  "errors",
  "messages",
] as const;

/** Pulls a row's exclusion reasons out, whatever the backend called them. */
export const reasonsOf = (record: BulkRecord): string[] => {
  for (const key of REASON_KEYS) {
    const value = record[key];
    if (Array.isArray(value)) return value.map((item) => String(item));
    if (typeof value === "string" && value.trim()) return [value];
  }
  return [];
};

/**
 * Column shown for an excluded row, taken from `originalData`.
 *
 * `originalData` is the operator's own sheet cell, `normalizedData` is what the
 * backend made of it — and they can disagree: a row typed as `AMENDMENT`
 * normalises to `PORTFOLIO_AGREEMENT`. Showing the normalised value would send
 * someone looking for a row that does not exist in their workbook, so the
 * original is what is displayed.
 */
export const EXCLUDED_COLUMNS = ["Portfolio Code", "Approval Type"] as const;

/**
 * Flattens excluded rows for display and export.
 *
 * They arrive nested (`sourceRow` / `originalData` / `normalizedData` /
 * `reasons`), which the record tables would otherwise render as JSON blobs.
 * `normalizedData` is dropped entirely — it is the backend's interpretation,
 * not something the operator can act on.
 */
export const flattenExcluded = (records: BulkRecord[]): BulkRecord[] =>
  records.map((record) => {
    const original = (record.originalData ?? {}) as Record<string, unknown>;
    const flat: BulkRecord = {
      Row: record.sourceRow ?? record.rowNumber ?? "",
    };
    EXCLUDED_COLUMNS.forEach((column) => {
      flat[column] = original[column] ?? "";
    });
    // Carry the reasons through under a key `reasonsOf` recognises.
    flat.reasons = reasonsOf(record);
    return flat;
  });

/** The reason field is rendered on its own, so it is dropped from the grid. */
export const dataColumns = (records: BulkRecord[]): string[] => {
  const seen: string[] = [];
  records.forEach((record) => {
    Object.keys(record).forEach((key) => {
      if (!seen.includes(key) && !REASON_KEYS.includes(key as never)) {
        seen.push(key);
      }
    });
  });
  return seen;
};
